/**
 * Business-level gate for the proactive printer-readiness feature.
 *
 * The Footer V4 readiness indicator, startup/background checks, and the
 * footer-triggered printer dialog must only run for businesses that have
 * printing enabled in the Account Access V2 settings policy:
 *
 *   business.access_v2.businessAccess.settings.printing.enabled === true
 *
 * Anything missing along that path (no policy, no `printing` block) is treated
 * as disabled — fail-secure. This does NOT gate the explicit Settings → System
 * → Printers screen, which follows its own existing behavior.
 */
import {
  AutomaticPrintModeV2,
  type BusinessDataV2Type,
} from '@am3nd-org/pointcraft-types';

import { useDeviceSyncStore } from 'src/store/DeviceSync';

/** Whether the proactive printer-readiness feature is enabled for `business`. */
export function isPrintingEnabled(business: BusinessDataV2Type | null | undefined): boolean {
  return business?.access_v2?.businessAccess?.settings?.printing?.enabled === true;
}

/** Reactive `isPrintingEnabled` for components. */
export function usePrintingEnabled(): boolean {
  return useDeviceSyncStore((state) => isPrintingEnabled(state.businessData));
}

export type AutomaticPrintJob = 'order' | 'receipt';

/**
 * Resolve the access-v2 success-print policy into its execution order.
 * Printing must be enabled explicitly; missing or invalid modes print nothing.
 * A combined Restaurant POS print is deliberately order-first so the kitchen
 * ticket reaches production before the customer receipt is emitted.
 */
export function getAutomaticPrintSequence(
  business: BusinessDataV2Type | null | undefined,
  options: { canPrintOrder: boolean }
): readonly AutomaticPrintJob[] {
  const printing = business?.access_v2?.businessAccess?.settings?.printing;
  if (printing?.enabled !== true) return [];

  switch (printing.automaticPrintMode) {
    case AutomaticPrintModeV2.RECEIPT:
      return ['receipt'];
    case AutomaticPrintModeV2.ORDER:
      return options.canPrintOrder ? ['order'] : [];
    case AutomaticPrintModeV2.RECEIPT_AND_ORDER:
      return options.canPrintOrder ? ['order', 'receipt'] : ['receipt'];
    case AutomaticPrintModeV2.NONE:
    default:
      return [];
  }
}
